const CTM_VISITOR_SID_PATTERN = /^[0-9a-f]{24}$/i;
const CTM_VISITOR_SESSION_PROMISES_KEY = Symbol.for(
  "ctm-chat.visitor-session-promises",
);
const CTM_VISITOR_CALLBACK_COUNTER_KEY = Symbol.for(
  "ctm-chat.visitor-session-callback-counter",
);
const CTM_VISITOR_COOKIE_MAX_AGE_SECONDS = 30 * 24 * 60 * 60;
const DEFAULT_BOOTSTRAP_TIMEOUT_MS = 1000;
const DEFAULT_SESSION_RECALL_ATTEMPTS = 2;

function parseAccountId(accountId) {
  const value = String(accountId ?? "").trim();
  if (!/^\d+$/.test(value)) {
    return null;
  }

  const parsed = Number(value);
  return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : null;
}

/**
 * Validates and normalizes a CTM tracking visitor SID for an account.
 */
export function normalizeCtmVisitorSid(visitorSid, accountId) {
  if (typeof visitorSid !== "string") {
    return null;
  }

  const normalizedSid = visitorSid.trim().toLowerCase();
  const normalizedAccountId = parseAccountId(accountId);
  if (
    normalizedAccountId === null ||
    !CTM_VISITOR_SID_PATTERN.test(normalizedSid)
  ) {
    return null;
  }

  const currentAccountId = Number.parseInt(normalizedSid.slice(8, 16), 16);
  const legacyAccountId = Number.parseInt(normalizedSid.slice(8, 15), 16);
  if (
    currentAccountId !== normalizedAccountId &&
    legacyAccountId !== normalizedAccountId
  ) {
    return null;
  }

  return normalizedSid;
}

function getCookieValue(name, documentRef) {
  if (!documentRef || typeof documentRef.cookie !== "string") {
    return null;
  }

  const prefix = `${name}=`;
  const cookie = documentRef.cookie
    .split(";")
    .map((value) => value.trim())
    .find((value) => value.startsWith(prefix));
  if (!cookie) {
    return null;
  }

  const value = cookie.slice(prefix.length);
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

/**
 * Applies the same marketing-consent convention as the standard CTM tracker.
 */
export function hasCtmMarketingConsent(
  scope = globalThis,
  documentRef = scope?.document || globalThis.document,
  runtime = scope?.__ctm,
) {
  const hasMarketingConsent =
    runtime?.hasMarketingConsent || scope?.__ctm?.hasMarketingConsent;
  if (typeof hasMarketingConsent === "function") {
    try {
      return hasMarketingConsent() !== false;
    } catch {
      return false;
    }
  }

  const rawConsent = getCookieValue("CookieConsent", documentRef);
  if (!rawConsent || rawConsent.charAt(0) !== "{") {
    return true;
  }

  try {
    return JSON.parse(rawConsent).marketing !== false;
  } catch {
    return true;
  }
}

/**
 * Reads the visitor SID exposed by an active, same-account CTM /t.js runtime.
 */
export function getActiveCtmVisitorSid(
  accountId,
  scope = globalThis,
) {
  const expectedAccountId = parseAccountId(accountId);
  if (expectedAccountId === null) {
    return null;
  }

  const accountRuntime = scope?.__ctm_accounts?.[expectedAccountId];
  const globalRuntime = scope?.__ctm;
  const runtime =
    parseAccountId(accountRuntime?.config?.aid) === expectedAccountId
      ? accountRuntime
      : globalRuntime;
  const config = runtime?.config;
  if (
    parseAccountId(config?.aid) !== expectedAccountId ||
    !hasCtmMarketingConsent(scope, scope?.document, runtime)
  ) {
    return null;
  }
  return normalizeCtmVisitorSid(config?.sid, expectedAccountId);
}

function addCtmReadyCallback(scope, callback) {
  if (!scope) {
    return;
  }

  const readyQueue = scope.__ctm_loaded || [];
  if (!scope.__ctm_loaded) {
    scope.__ctm_loaded = readyQueue;
  }
  if (typeof readyQueue.push === "function") {
    readyQueue.push(callback);
  }
}

/**
 * Captures the visitor SID now or when /t.js invokes its standard ready queue.
 */
export function observeActiveCtmVisitorSid(
  accountId,
  onSession,
  scope = globalThis,
) {
  const capture = () => {
    const visitorSid = getActiveCtmVisitorSid(accountId, scope);
    if (visitorSid && typeof onSession === "function") {
      onSession(visitorSid);
    }
    return visitorSid;
  };

  if (!capture()) {
    addCtmReadyCallback(scope, capture);
  }
}

function hasCtmTrackingScript(documentRef) {
  if (!documentRef?.scripts) {
    return false;
  }

  return Array.from(documentRef.scripts).some((script) => {
    const src = script?.src || script?.getAttribute?.("src") || "";
    return /\/(?:t|m)\.js(?:[?#]|$)/i.test(src);
  });
}

/**
 * Waits briefly only when a CTM /t.js or /m.js runtime has not finished loading.
 */
export function waitForActiveCtmVisitorSid(
  accountId,
  {
    scope = globalThis,
    documentRef = globalThis.document,
    timeoutMs = 1000,
  } = {},
) {
  const activeVisitorSid = getActiveCtmVisitorSid(accountId, scope);
  if (activeVisitorSid) {
    return Promise.resolve(activeVisitorSid);
  }

  if (!scope?.__ctm && !hasCtmTrackingScript(documentRef)) {
    return Promise.resolve(null);
  }

  return new Promise((resolve) => {
    let settled = false;
    let timeoutId = null;
    const clearTimer = scope?.clearTimeout?.bind(scope) || clearTimeout;
    const setTimer = scope?.setTimeout?.bind(scope) || setTimeout;

    const finish = (visitorSid) => {
      if (settled) {
        return;
      }
      settled = true;
      if (timeoutId !== null) {
        clearTimer(timeoutId);
      }
      resolve(visitorSid || null);
    };

    const capture = () => {
      const visitorSid = getActiveCtmVisitorSid(accountId, scope);
      if (visitorSid) {
        finish(visitorSid);
      }
    };

    addCtmReadyCallback(scope, capture);
    capture();
    if (!settled) {
      timeoutId = setTimer(() => {
        finish(getActiveCtmVisitorSid(accountId, scope));
      }, Math.max(0, timeoutMs));
    }
  });
}

function normalizeTrackingHost(trackingHost, scope) {
  if (typeof trackingHost !== "string" || !trackingHost.trim()) {
    return null;
  }

  try {
    const Url = scope?.URL || URL;
    const baseUrl = scope?.location?.href || "https://localhost/";
    const url = new Url(trackingHost, baseUrl);
    if (
      (url.protocol !== "https:" && url.protocol !== "http:") ||
      url.username ||
      url.password
    ) {
      return null;
    }
    return url.origin;
  } catch {
    return null;
  }
}

function getCachedCtmVisitorSid(accountId, documentRef) {
  const cookieNames = [`__ctmid_${accountId}`, "__ctmid"];
  for (const cookieName of cookieNames) {
    const visitorSid = normalizeCtmVisitorSid(
      getCookieValue(cookieName, documentRef),
      accountId,
    );
    if (visitorSid) {
      return visitorSid;
    }
  }
  return null;
}

function setVisitorCookie(name, visitorSid, scope, documentRef) {
  if (!documentRef) {
    return;
  }

  const protocol =
    scope?.location?.protocol || documentRef.location?.protocol || "";
  const secure = protocol === "https:" ? "; Secure" : "";
  documentRef.cookie = `${name}=${visitorSid}; path=/; max-age=${CTM_VISITOR_COOKIE_MAX_AGE_SECONDS}; SameSite=Lax${secure}`;
}

function cacheCtmVisitorSid(accountId, visitorSid, scope, documentRef) {
  setVisitorCookie(`__ctmid_${accountId}`, visitorSid, scope, documentRef);

  const legacyCookie = getCookieValue("__ctmid", documentRef);
  const normalizedLegacyCookie =
    typeof legacyCookie === "string" &&
    CTM_VISITOR_SID_PATTERN.test(legacyCookie.trim())
      ? legacyCookie.trim().toLowerCase()
      : null;
  if (
    !normalizedLegacyCookie ||
    normalizeCtmVisitorSid(normalizedLegacyCookie, accountId)
  ) {
    setVisitorCookie("__ctmid", visitorSid, scope, documentRef);
  }
}

function nextSessionCallbackName(accountId, scope) {
  const current = Number(scope[CTM_VISITOR_CALLBACK_COUNTER_KEY]) || 0;
  const next = current + 1;
  scope[CTM_VISITOR_CALLBACK_COUNTER_KEY] = next;
  return `__ctmChatSession${accountId}_${next}`;
}

function removeScript(script) {
  if (script?.parentNode?.removeChild) {
    script.parentNode.removeChild(script);
  }
}

/**
 * Recalls a visitor SID and retries once when the tracking script fails to load.
 */
async function recallCtmVisitorSid(
  accountId,
  trackingHost,
  cachedVisitorSid,
  {
    scope,
    documentRef,
    timeoutMs,
  },
) {
  const parent = documentRef?.head || documentRef?.documentElement;
  if (!parent?.appendChild || !documentRef?.createElement) {
    return null;
  }

  for (
    let attempt = 0;
    attempt < DEFAULT_SESSION_RECALL_ATTEMPTS;
    attempt += 1
  ) {
    const result = await new Promise((resolve) => {
      const callbackName = nextSessionCallbackName(accountId, scope);
      const Url = scope?.URL || URL;
      const url = new Url("/s.js", `${trackingHost}/`);
      url.searchParams.set("callback", callbackName);
      url.searchParams.set(
        "loc",
        String(scope?.location?.href || documentRef.location?.href || ""),
      );
      url.searchParams.set("ref", String(documentRef.referrer || ""));
      url.searchParams.set("mc", "1");
      if (cachedVisitorSid) {
        url.searchParams.set("sid", cachedVisitorSid);
      }

      const script = documentRef.createElement("script");
      let settled = false;
      let timeoutId = null;
      const clearTimer = scope?.clearTimeout?.bind(scope) || clearTimeout;
      const setTimer = scope?.setTimeout?.bind(scope) || setTimeout;

      const cleanup = () => {
        if (timeoutId !== null) {
          clearTimer(timeoutId);
        }
        try {
          delete scope[callbackName];
        } catch {
          scope[callbackName] = undefined;
        }
        removeScript(script);
      };

      const finish = (visitorSid, retryable = false) => {
        if (settled) {
          return;
        }
        settled = true;
        cleanup();
        resolve({ visitorSid: visitorSid || null, retryable });
      };

      scope[callbackName] = (payload) => {
        if (parseAccountId(payload?.aid) !== accountId) {
          finish(null);
          return;
        }
        finish(normalizeCtmVisitorSid(payload?.sid, accountId));
      };

      script.async = true;
      script.onerror = () => finish(null, true);
      script.src = url.toString();
      timeoutId = setTimer(
        () => finish(null, true),
        Math.max(0, timeoutMs),
      );
      try {
        parent.appendChild(script);
      } catch {
        finish(null, true);
      }
    });

    if (result.visitorSid || !result.retryable) {
      return result.visitorSid;
    }
  }

  return null;
}

async function postCtmVisitorPageview(
  accountId,
  visitorSid,
  trackingHost,
  {
    scope,
    documentRef,
    fetchFn,
    timeoutMs,
  },
) {
  if (typeof fetchFn !== "function") {
    return false;
  }

  const pageview = {
    aid: accountId,
    sid: visitorSid,
    ref: String(documentRef?.referrer || ""),
    url: String(scope?.location?.href || documentRef?.location?.href || ""),
    nums: [],
    evt: "pageview",
  };
  if (documentRef?.title) {
    pageview.t = documentRef.title;
  }

  const clearTimer = scope?.clearTimeout?.bind(scope) || clearTimeout;
  const setTimer = scope?.setTimeout?.bind(scope) || setTimeout;
  let timeoutId = null;
  const request = Promise.resolve()
    .then(() =>
      fetchFn(`${trackingHost}/x.json`, {
        method: "POST",
        mode: "cors",
        credentials: "omit",
        keepalive: true,
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(pageview),
      }),
    )
    .then((response) => {
      if (response?.ok === true) {
        return true;
      }
      const status = Number(response?.status);
      return status >= 200 && status < 300;
    })
    .catch(() => false);
  const timedOut = new Promise((resolve) => {
    timeoutId = setTimer(
      () => resolve(false),
      Math.max(0, timeoutMs),
    );
  });

  const tracked = await Promise.race([request, timedOut]);
  if (timeoutId !== null) {
    clearTimer(timeoutId);
  }
  return tracked;
}

async function bootstrapCtmVisitorSid(
  accountId,
  trackingHost,
  options,
) {
  const {
    scope,
    documentRef,
    fetchFn,
    timeoutMs,
  } = options;
  if (!hasCtmMarketingConsent(scope, documentRef)) {
    return null;
  }

  const cachedVisitorSid = getCachedCtmVisitorSid(accountId, documentRef);
  const visitorSid = await recallCtmVisitorSid(
    accountId,
    trackingHost,
    cachedVisitorSid,
    options,
  );
  if (
    !visitorSid ||
    !hasCtmMarketingConsent(scope, documentRef)
  ) {
    return null;
  }

  cacheCtmVisitorSid(accountId, visitorSid, scope, documentRef);
  await postCtmVisitorPageview(
    accountId,
    visitorSid,
    trackingHost,
    {
      scope,
      documentRef,
      fetchFn,
      timeoutMs,
    },
  );
  // Keep the recalled SID when the pageview acknowledgement is delayed or
  // unavailable. FormReactor can still initialize it with the parent URLs.
  return visitorSid;
}

/**
 * Creates a CTM tracking visitor for a chat-only embed and records its pageview.
 */
export function getOrCreateCtmVisitorSid(
  accountId,
  trackingHost,
  {
    scope = globalThis,
    documentRef = scope?.document || globalThis.document,
    fetchFn = scope?.fetch?.bind(scope),
    timeoutMs = DEFAULT_BOOTSTRAP_TIMEOUT_MS,
  } = {},
) {
  const normalizedAccountId = parseAccountId(accountId);
  const normalizedTrackingHost = normalizeTrackingHost(trackingHost, scope);
  if (
    normalizedAccountId === null ||
    !normalizedTrackingHost ||
    !hasCtmMarketingConsent(scope, documentRef)
  ) {
    return Promise.resolve(null);
  }

  let sessionPromises = scope[CTM_VISITOR_SESSION_PROMISES_KEY];
  if (!(sessionPromises instanceof Map)) {
    sessionPromises = new Map();
    scope[CTM_VISITOR_SESSION_PROMISES_KEY] = sessionPromises;
  }

  const key = `${normalizedAccountId}:${normalizedTrackingHost}`;
  const pendingSession = sessionPromises.get(key);
  if (pendingSession) {
    return pendingSession;
  }

  const sessionPromise = bootstrapCtmVisitorSid(
    normalizedAccountId,
    normalizedTrackingHost,
    {
      scope,
      documentRef,
      fetchFn,
      timeoutMs: Math.max(0, Number(timeoutMs) || 0),
    },
  )
    .catch(() => null)
    .then((visitorSid) => {
      if (!visitorSid) {
        sessionPromises.delete(key);
      }
      return visitorSid;
    });
  sessionPromises.set(key, sessionPromise);
  return sessionPromise;
}
